import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    X,
    Save,
    Copy,
    Eye,
    EyeOff,
    RotateCw,
    AlertCircle,
    CheckCircle2,
    Plus,
    Trash2,
    Edit,
} from "lucide-react";
import {
    Account,
    DiscordChannel
} from "../../interfaces/AccountData.ts";
import "./AccountModal.css";
import { mapCompositeToAccount, mapAccountToComposite } from "../../components/AccountHelpers.ts";
import { useAuth } from "./../../contexts/AuthContext.js";


interface AccountModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;  // ← NEW: Pass updated account data back
    account?: Account;
}

const defaultFormData: Account = {
    id: "",
    name: "",
    type: "Personal",
    isActive: true,
    isTestnet: false,
    apiKey: "",
    apiSecret: "",
    isGlobalAccount: false,
    fullSizeTradePercentage: 0,
    lottoTradePercentage: 0,
    sizeMarginOfErrorPercentage: 0,
    weeklyAllocation: 0,
    opraFeedEnabled: false,
    sellsDisabled: false,
    fullSizeSellEnabled: false,
    sameDayExpirationLottoEnabled: false,
    balanceTrackingEnabled: false,
    preMarketClosePositionClearEnabled: false,
    profitTriggerPercentage: 10,
    lossTriggerPercentage: 5,
    balanceTrackingTriggerTimeoutHours: 24,
    discountedBuyPercentage: 0,
    discordChannels: [],
};

const defaultDiscordChannel: Omit<DiscordChannel, "id"> = {
    channelId: "",
    enabled: true,
    tradeSizePercentage: 0.001,
};

const AccountModal: React.FC<AccountModalProps> = ({ isOpen, onClose, onSave, account }) => {
    const [formData, setFormData] = useState<Account>(defaultFormData);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showApiKey, setShowApiKey] = useState(false);
    const [showApiSecret, setShowApiSecret] = useState(false);
    const [apiKeyCopied, setApiKeyCopied] = useState(false);
    const [apiSecretCopied, setApiSecretCopied] = useState(false);
    const [editingDiscordChannel, setEditingDiscordChannel] = useState<DiscordChannel | null>(null);
    const [isAddingChannel, setIsAddingChannel] = useState(false);
    const [tempDiscordChannel, setTempDiscordChannel] = useState<Omit<DiscordChannel, "id">>(defaultDiscordChannel);
    const [discordChannelErrors, setDiscordChannelErrors] = useState<Record<string, string>>({});
    const [discordChannels, setDiscordChannels] = useState([]);

    const { user } = useAuth();

    const isEditMode = !!account?.id;

    useEffect(() => {
        if (account) {
            console.log("acc");
            console.log(account);
            setFormData(account);
        } else {
            setFormData(defaultFormData);
        }
    }, [account]);

    useEffect(() => {
        axios.get("/discord-channels")
            .then(response => {
                const channels = response.data.map((ch: any) => ({
                    id: ch.id,             // use lowercase property names
                    channelId: ch.id,
                    name: ch.name,
                }));
                setDiscordChannels(channels);
                console.log("Fetched channels:", channels);
            })
            .catch(error => {
                console.error("Error fetching Discord channels:", error);
            });
    }, []);


    useEffect(() => {
        if (apiKeyCopied) {
            const timer = setTimeout(() => setApiKeyCopied(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [apiKeyCopied]);

    useEffect(() => {
        if (apiSecretCopied) {
            const timer = setTimeout(() => setApiSecretCopied(false), 2000);
            return () => clearTimeout(timer);
        }
    }, [apiSecretCopied]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        let newValue = value;
        if (type === "number") {
            newValue = value.replace(",", ".");
        }
        setFormData((prev) => {
            const updated = {
                ...prev,
                [name]:
                    type === "checkbox"
                        ? checked
                        : type === "number"
                            ? parseFloat(newValue) || 0
                            : newValue,
            };
            if (name === "type") {
                updated.isGlobalAccount = value === "Shared";
            }
            return updated;
        });
        if (errors[name]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const copyToClipboard = (text: string, field: "apiKey" | "apiSecret") => {
        navigator.clipboard.writeText(text).then(
            () => {
                if (field === "apiKey") {
                    setApiKeyCopied(true);
                } else {
                    setApiSecretCopied(true);
                }
            },
            (err) => {
                console.error("Could not copy text: ", err);
            }
        );
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) {
            newErrors.name = "Account name is required";
        }
        if (!formData.apiKey.trim()) {
            newErrors.apiKey = "API Key is required";
        }
        if (!formData.apiSecret.trim()) {
            newErrors.apiSecret = "API Secret is required";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateDiscordChannel = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!tempDiscordChannel.channelId) {
            newErrors.channelId = "Discord channel is required";
        }
        if (tempDiscordChannel.tradeSizePercentage < 0) {
            newErrors.tradeSizePercentage = "Trade size must be greater than or equal to 0";
        }
        setDiscordChannelErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Build and send the composite update request with all fields.
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateForm()) return;

        const dataToSave = !isEditMode
            ? { ...formData, id: `acc-${Date.now().toString(36)}` }
            : formData;

        const payload = {
            AccountId: parseInt(dataToSave.id) || 0,
            Name: dataToSave.name,
            Active: dataToSave.isActive,
            ApiKey: dataToSave.apiKey,
            ApiSecret: dataToSave.apiSecret,
            IsGlobal: dataToSave.isGlobalAccount,
            IsTestnet: dataToSave.isTestnet,
            WeeklyAllocation: dataToSave.weeklyAllocation || 0,
            FullSizeTradePercentage: dataToSave.fullSizeTradePercentage,
            LottoTradePercentage: dataToSave.lottoTradePercentage,
            SizeMarginOfErrorPercentage: dataToSave.sizeMarginOfErrorPercentage,
            OpraFeedEnabled: dataToSave.opraFeedEnabled,
            SellsDisabled: dataToSave.sellsDisabled,
            FullSizeSellEnabled: dataToSave.fullSizeSellEnabled,
            SameDayExpirationLottoEnabled: dataToSave.sameDayExpirationLottoEnabled,
            BalanceTrackingEnabled: dataToSave.balanceTrackingEnabled,
            PreMarketClosePositionClearEnabled: dataToSave.preMarketClosePositionClearEnabled,
            DiscountedBuyPercentage: dataToSave.discountedBuyPercentage || 0,
            ProfitTriggerPercentage: dataToSave.profitTriggerPercentage,
            LossTriggerPercentage: dataToSave.lossTriggerPercentage,
            BalanceTrackingTriggerTimeout: dataToSave.balanceTrackingTriggerTimeoutHours ?
                (dataToSave.balanceTrackingTriggerTimeoutHours >= 24
                    ? `${Math.floor(dataToSave.balanceTrackingTriggerTimeoutHours / 24)}.${String(dataToSave.balanceTrackingTriggerTimeoutHours % 24).padStart(2, '0')}:00:00`
                    : `${String(dataToSave.balanceTrackingTriggerTimeoutHours).padStart(2, '0')}:00:00`)
                : null,
            DiscordChannelSettings: dataToSave.discordChannels.map((dc) => ({
                Id: parseInt(dc.id) || 0,
                DiscordChannelId: dc.channelId,
                IsEnabled: dc.enabled,
                TradeSizeMultiplier: dc.tradeSizePercentage,
            })),
        };

        console.log("isinedit");
        console.log(isEditMode);
        if (isEditMode) {
            try {
                await axios.post("/account/update/composite", payload);
                onClose();
                onSave();
            } catch (error) {
                console.error("Error updating account composite:", error);
                alert("Failed to update account composite.");
            }
        }
        else {
            try {
                await axios.post("/account/create/composite", payload);
                onClose();
                onSave();
            } catch (error) {
                console.error("Error creating account composite:", error);
                alert("Failed to create account composite.");
            }
        }


    };

    const handleUpdateWeeklyAllocation = async () => {
        try {
            console.log(formData.discordChannels);
            const accountId = parseInt(formData.id) || 0;
            const response = await axios.get("/account/weeklyallocation", { params: { accountId } });
            // Adjust based on your API response structure.
            const newWeeklyAllocation = response.data.data ?? response.data;
            setFormData(prev => ({ ...prev, weeklyAllocation: newWeeklyAllocation }));
        } catch (error) {
            console.error("Error updating weekly allocation:", error);
            alert("Failed to update weekly allocation.");
        }
    };


    const handleDiscordChannelChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        let newValue = value;
        if (type === "number") {
            newValue = value.replace(",", ".");
        }
        setTempDiscordChannel((prev) => ({
            ...prev,
            [name]:
                type === "checkbox"
                    ? checked
                    : type === "number"
                        ? parseFloat(newValue) || 0
                        : newValue,
        }));
        if (discordChannelErrors[name]) {
            setDiscordChannelErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const startAddDiscordChannel = () => {
        setIsAddingChannel(true);
        setTempDiscordChannel(defaultDiscordChannel);
        setEditingDiscordChannel(null);
        setDiscordChannelErrors({});
    };

    const startEditDiscordChannel = (channel: DiscordChannel) => {
        setEditingDiscordChannel(channel);
        setTempDiscordChannel({
            channelId: channel.channelId,
            enabled: channel.enabled,
            tradeSizePercentage: channel.tradeSizePercentage,
        });
        setIsAddingChannel(false);
        setDiscordChannelErrors({});
    };

    const cancelDiscordChannelEdit = () => {
        setIsAddingChannel(false);
        setEditingDiscordChannel(null);
        setTempDiscordChannel(defaultDiscordChannel);
        setDiscordChannelErrors({});
    };

    const saveDiscordChannel = () => {
        if (!validateDiscordChannel()) return;
        setFormData((prev) => {
            let updatedChannels;
            if (isAddingChannel) {
                const newChannel: DiscordChannel = {
                    ...tempDiscordChannel,
                    id: `dc-${Date.now().toString(36)}`,
                };
                updatedChannels = [...prev.discordChannels, newChannel];
            } else if (editingDiscordChannel) {
                updatedChannels = prev.discordChannels.map((channel) =>
                    channel.id === editingDiscordChannel.id
                        ? { ...channel, ...tempDiscordChannel }
                        : channel
                );
            } else {
                updatedChannels = prev.discordChannels;
            }
            return { ...prev, discordChannels: updatedChannels };
        });
        setIsAddingChannel(false);
        setEditingDiscordChannel(null);
        setTempDiscordChannel(defaultDiscordChannel);
    };

    const deleteDiscordChannel = (id: string) => {
        setFormData((prev) => ({
            ...prev,
            discordChannels: prev.discordChannels.filter((channel) => channel.id !== id),
        }));
    };

    const getChannelName = (channelId: string) => {
        const channel = discordChannels.find((c) => c.id === channelId);
        return channel ? channel.name : channelId;
    };

    if (!isOpen) return null;

    return (
        <div className="accountModal-backdrop">
            <div className="accountModal-container">
                <div className="accountModal-header">
                    <h2 className="accountModal-title">
                        {isEditMode ? "Edit Account" : "Create New Account"}
                    </h2>
                    <div className="accountModal-actions">
                        <button className="accountModal-save-button" onClick={handleSubmit} aria-label="Save account">
                            <Save size={18} />
                            <span>Save</span>
                        </button>
                        <button className="accountModal-close-button" onClick={onClose} aria-label="Close modal">
                            <X size={20} />
                        </button>
                    </div>
                </div>
                <form className="accountModal-form" onSubmit={handleSubmit}>
                    <div className="accountModal-sections">
                        <section className="accountModal-section">
                            <h3 className="accountModal-section-title">Account Details</h3>
                            <div className="accountModal-form-row">
                                <div className="accountModal-form-group">
                                    <label htmlFor="name" className="accountModal-label">Account Name</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className={`accountModal-input ${errors.name ? "accountModal-input-error" : ""}`}
                                        placeholder="Enter account name"
                                    />
                                    {errors.name && (
                                        <span className="accountModal-error">
                                            <AlertCircle size={12} /> {errors.name}
                                        </span>
                                    )}
                                </div>
                                {(!account || account.ownerUserId === user.user.userId.value) && (
                                    <div className="accountModal-form-group">
                                        <label htmlFor="type" className="accountModal-label">Account Type</label>
                                        <select
                                            id="type"
                                            name="type"
                                            value={formData.type}
                                            onChange={handleChange}
                                            className="accountModal-select"
                                        >
                                            <option value="Personal">Private</option>
                                            <option value="Shared">Shared</option>
                                        </select>
                                    </div>
                                )}

                                <div className="accountModal-toggle-container">
                                    <div className="accountModal-toggle-group">
                                        <label className="accountModal-toggle-label">
                                            <span>Active</span>
                                            <div className="accountModal-toggle-wrapper">
                                                <input
                                                    type="checkbox"
                                                    name="isActive"
                                                    checked={formData.isActive}
                                                    onChange={handleChange}
                                                    className="accountModal-toggle-input"
                                                />
                                                <div className="accountModal-toggle"></div>
                                            </div>
                                        </label>
                                    </div>
                                    <div className="accountModal-toggle-group">
                                        <label className="accountModal-toggle-label">
                                            <span>Testnet</span>
                                            <div className="accountModal-toggle-wrapper">
                                                <input
                                                    type="checkbox"
                                                    name="isTestnet"
                                                    checked={formData.isTestnet}
                                                    onChange={handleChange}
                                                    className="accountModal-toggle-input"
                                                />
                                                <div className="accountModal-toggle"></div>
                                            </div>
                                        </label>
                                    </div>
                                    {/*<div className="accountModal-toggle-group">*/}
                                    {/*    <label className="accountModal-toggle-label">*/}
                                    {/*        <span>Global Account</span>*/}
                                    {/*        <div className="accountModal-toggle-wrapper">*/}
                                    {/*            <input*/}
                                    {/*                type="checkbox"*/}
                                    {/*                name="isGlobalAccount"*/}
                                    {/*                checked={formData.isGlobalAccount}*/}
                                    {/*                onChange={handleChange}*/}
                                    {/*                className="accountModal-toggle-input"*/}
                                    {/*            />*/}
                                    {/*            <div className="accountModal-toggle"></div>*/}
                                    {/*        </div>*/}
                                    {/*    </label>*/}
                                    {/*</div>*/}
                                </div>
                                <div className="accountModal-form-group accountModal-secret-field">
                                    <label htmlFor="apiKey" className="accountModal-label">API Key</label>
                                    <div className="accountModal-input-group">
                                        <input
                                            type={showApiKey ? "text" : "password"}
                                            id="apiKey"
                                            name="apiKey"
                                            value={formData.apiKey}
                                            onChange={handleChange}
                                            className={`accountModal-input ${errors.apiKey ? "accountModal-input-error" : ""}`}
                                            placeholder="Enter API key"
                                        />
                                        <button
                                            type="button"
                                            className="accountModal-icon-button"
                                            onClick={() => setShowApiKey(!showApiKey)}
                                            aria-label={showApiKey ? "Hide API key" : "Show API key"}
                                        >
                                            {showApiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                        <button
                                            type="button"
                                            className="accountModal-icon-button"
                                            onClick={() => copyToClipboard(formData.apiKey, "apiKey")}
                                            aria-label="Copy API key"
                                        >
                                            <Copy size={16} />
                                        </button>
                                    </div>
                                    {errors.apiKey && (
                                        <span className="accountModal-error">
                                            <AlertCircle size={12} /> {errors.apiKey}
                                        </span>
                                    )}
                                    {apiKeyCopied && (
                                        <span className="accountModal-copied">
                                            <CheckCircle2 size={12} /> Copied!
                                        </span>
                                    )}
                                </div>
                                <div className="accountModal-form-group accountModal-secret-field">
                                    <label htmlFor="apiSecret" className="accountModal-label">API Secret</label>
                                    <div className="accountModal-input-group">
                                        <input
                                            type={showApiSecret ? "text" : "password"}
                                            id="apiSecret"
                                            name="apiSecret"
                                            value={formData.apiSecret}
                                            onChange={handleChange}
                                            className={`accountModal-input ${errors.apiSecret ? "accountModal-input-error" : ""}`}
                                            placeholder="Enter API secret"
                                        />
                                        <button
                                            type="button"
                                            className="accountModal-icon-button"
                                            onClick={() => setShowApiSecret(!showApiSecret)}
                                            aria-label={showApiSecret ? "Hide API secret" : "Show API secret"}
                                        >
                                            {showApiSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                        <button
                                            type="button"
                                            className="accountModal-icon-button"
                                            onClick={() => copyToClipboard(formData.apiSecret, "apiSecret")}
                                            aria-label="Copy API secret"
                                        >
                                            <Copy size={16} />
                                        </button>
                                    </div>
                                    {errors.apiSecret && (
                                        <span className="accountModal-error">
                                            <AlertCircle size={12} /> {errors.apiSecret}
                                        </span>
                                    )}
                                    {apiSecretCopied && (
                                        <span className="accountModal-copied">
                                            <CheckCircle2 size={12} /> Copied!
                                        </span>
                                    )}
                                </div>
                            </div>
                        </section>
                        <section className="accountModal-section">
                            <h3 className="accountModal-section-title">Trade Settings</h3>
                            <div className="accountModal-form-row">
                                <div className="accountModal-toggle-group">
                                    <label className="accountModal-toggle-label">
                                        <span>Sells Disabled</span>
                                        <div className="accountModal-toggle-wrapper">
                                            <input
                                                type="checkbox"
                                                name="sellsDisabled"
                                                checked={formData.sellsDisabled}
                                                onChange={handleChange}
                                                className="accountModal-toggle-input"
                                            />
                                            <div className="accountModal-toggle"></div>
                                        </div>
                                    </label>
                                </div>
                                <div className="accountModal-toggle-group">
                                    <label className="accountModal-toggle-label">
                                        <span>OPRA Feed</span>
                                        <div className="accountModal-toggle-wrapper">
                                            <input
                                                type="checkbox"
                                                name="opraFeedEnabled"
                                                checked={formData.opraFeedEnabled}
                                                onChange={handleChange}
                                                className="accountModal-toggle-input"
                                            />
                                            <div className="accountModal-toggle"></div>
                                        </div>
                                    </label>
                                </div>
                                <div className="accountModal-toggle-group">
                                    <label className="accountModal-toggle-label">
                                        <span>Full Position Sell</span>
                                        <div className="accountModal-toggle-wrapper">
                                            <input
                                                type="checkbox"
                                                name="fullSizeSellEnabled"
                                                checked={formData.fullSizeSellEnabled}
                                                onChange={handleChange}
                                                className="accountModal-toggle-input"
                                            />
                                            <div className="accountModal-toggle"></div>
                                        </div>
                                    </label>
                                </div>
                                <div className="accountModal-toggle-group">
                                    <label className="accountModal-toggle-label">
                                        <span>Same Day Expiration Lotto</span>
                                        <div className="accountModal-toggle-wrapper">
                                            <input
                                                type="checkbox"
                                                name="sameDayExpirationLottoEnabled"
                                                checked={formData.sameDayExpirationLottoEnabled}
                                                onChange={handleChange}
                                                className="accountModal-toggle-input"
                                            />
                                            <div className="accountModal-toggle"></div>
                                        </div>
                                    </label>
                                </div>
                                <div className="accountModal-form-group">
                                    <label htmlFor="fullSizeTradePercentage" className="accountModal-label">Full-Size Trade %</label>
                                    <input
                                        type="number"
                                        id="fullSizeTradePercentage"
                                        name="fullSizeTradePercentage"
                                        value={formData.fullSizeTradePercentage}
                                        onChange={handleChange}
                                        className={`accountModal-input ${errors.fullSizeTradePercentage ? "accountModal-input-error" : ""}`}
                                        min="0"
                                        step="1"
                                        lang="en-US"
                                    />
                                </div>
                                <div className="accountModal-form-group">
                                    <label htmlFor="lottoTradePercentage" className="accountModal-label">Lotto Trade %</label>
                                    <input
                                        type="number"
                                        id="lottoTradePercentage"
                                        name="lottoTradePercentage"
                                        value={formData.lottoTradePercentage}
                                        onChange={handleChange}
                                        className={`accountModal-input ${errors.lottoTrade ? "accountModal-input-error" : ""}`}
                                        min="0"
                                        step="0.5"
                                    />
                                </div>
                                <div className="accountModal-form-group">
                                    <label htmlFor="sizeMarginOfErrorPercentage" className="accountModal-label">Size Margin of Error %</label>
                                    <input
                                        type="number"
                                        id="sizeMarginOfErrorPercentage"
                                        name="sizeMarginOfErrorPercentage"
                                        value={formData.sizeMarginOfErrorPercentage}
                                        onChange={handleChange}
                                        className={`accountModal-input ${errors.sizeMarginOfErrorPercentage ? "accountModal-input-error" : ""}`}
                                        min="0"
                                        step="0.5"
                                    />
                                </div>
                                <div className="accountModal-form-group">
                                    <label htmlFor="discountedBuyPercentage" className="accountModal-label">Discounted Buy %</label>
                                    <input
                                        type="number"
                                        id="discountedBuyPercentage"
                                        name="discountedBuyPercentage"
                                        value={formData.discountedBuyPercentage || 0}
                                        onChange={handleChange}
                                        className={`accountModal-input ${errors.discountedBuyPercentage ? "accountModal-input-error" : ""}`}
                                        min="0"
                                        step="1"
                                    />
                                </div>
                                <div className="accountModal-form-group">
                                    <label htmlFor="weeklyAllocation" className="accountModal-label">Weekly Allocation</label>
                                    <input
                                        type="number"
                                        id="weeklyAllocation"
                                        name="weeklyAllocation"
                                        value={formData.weeklyAllocation}
                                        onChange={handleChange}
                                        className={`accountModal-input ${errors.weeklyAllocation ? "accountModal-input-error" : ""}`}
                                        min="0"
                                        step="1"
                                        lang="en-US"
                                    />
                                </div>
                                <div className="accountModal-form-group">
                                    <button type="button" className="accountModal-recalculate-button" onClick={handleUpdateWeeklyAllocation}>
                                        <RotateCw size={16} />
                                        <span>Update Weekly Allocation</span>
                                    </button>
                                </div>

                            </div>
                        </section>
                        <section className="accountModal-section">
                            <h3 className="accountModal-section-title">Position Clear Settings</h3>
                            <div className="accountModal-form-row">
                                <div className="accountModal-toggle-group">
                                    <label className="accountModal-toggle-label">
                                        <span>Close all positions 3 minutes before market close</span>
                                        <div className="accountModal-toggle-wrapper">
                                            <input
                                                type="checkbox"
                                                name="preMarketClosePositionClearEnabled"
                                                checked={formData.preMarketClosePositionClearEnabled}
                                                onChange={handleChange}
                                                className="accountModal-toggle-input"
                                            />
                                            <div className="accountModal-toggle"></div>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        </section>
                        <section className="accountModal-section">
                            <h3 className="accountModal-section-title">Balance Tracking Settings</h3>
                            <div className="accountModal-form-row">
                                <div className="accountModal-toggle-group">
                                    <label className="accountModal-toggle-label">
                                        <span>Balance Tracking Enabled</span>
                                        <div className="accountModal-toggle-wrapper">
                                            <input
                                                type="checkbox"
                                                name="balanceTrackingEnabled"
                                                checked={formData.balanceTrackingEnabled}
                                                onChange={handleChange}
                                                className="accountModal-toggle-input"
                                            />
                                            <div className="accountModal-toggle"></div>
                                        </div>
                                    </label>
                                </div>
                                {formData.balanceTrackingEnabled && (
                                    <>
                                        <div className="accountModal-form-group">
                                            <label htmlFor="profitTriggerPercentage" className="accountModal-label">Profit Trigger %</label>
                                            <input
                                                type="number"
                                                id="profitTriggerPercentage"
                                                name="profitTriggerPercentage"
                                                value={formData.profitTriggerPercentage || ""}
                                                onChange={handleChange}
                                                className={`accountModal-input ${errors.profitTriggerPercentage ? "accountModal-input-error" : ""}`}
                                                min="0"
                                                step="0.5"
                                                placeholder="e.g. 10"
                                            />
                                        </div>
                                        <div className="accountModal-form-group">
                                            <label htmlFor="lossTriggerPercentage" className="accountModal-label">Loss Trigger %</label>
                                            <input
                                                type="number"
                                                id="lossTriggerPercentage"
                                                name="lossTriggerPercentage"
                                                value={formData.lossTriggerPercentage || ""}
                                                onChange={handleChange}
                                                className={`accountModal-input ${errors.lossTriggerPercentage ? "accountModal-input-error" : ""}`}
                                                min="0"
                                                step="0.5"
                                                placeholder="e.g. 5"
                                            />
                                        </div>
                                        <div className="accountModal-form-group">
                                            <label htmlFor="balanceTrackingTriggerTimeoutHours" className="accountModal-label">Freeze Timeout (Hours)</label>
                                            <input
                                                type="number"
                                                id="balanceTrackingTriggerTimeoutHours"
                                                name="balanceTrackingTriggerTimeoutHours"
                                                value={formData.balanceTrackingTriggerTimeoutHours || ""}
                                                onChange={handleChange}
                                                className={`accountModal-input ${errors.balanceTrackingTriggerTimeoutHours ? "accountModal-input-error" : ""}`}
                                                min="1"
                                                step="1"
                                                placeholder="e.g. 24"
                                            />
                                        </div>
                                        {formData.willUnfreezeAt && (
                                            <div className="accountModal-form-group">
                                                <label className="accountModal-label">Account Status</label>
                                                <div className="accountModal-status-info">
                                                    <span className="accountModal-status-frozen">🔒 Account Frozen</span>
                                                </div>
                                            </div>
                                        )}
                                        {formData.marketOpenBalance && (
                                            <div className="accountModal-form-group">
                                                <label className="accountModal-label">Market Open Balance</label>
                                                <input
                                                    type="number"
                                                    id="marketOpenBalance"
                                                    name="marketOpenBalance"
                                                    value={formData.marketOpenBalance}
                                                    className={`accountModal-input ${errors.marketOpenBalance ? "accountModal-input-error" : ""}`}
                                                    min="0"
                                                    step="1"
                                                    lang="en-US"
                                                    readOnly
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </section>
                        <section className="accountModal-section">
                            <h3 className="accountModal-section-title">Discord Settings</h3>
                            <div className="accountModal-form-row">
                                <div className="accountModal-discord-channels">
                                    {formData.discordChannels.length > 0 ? (
                                        <div className="accountModal-channel-list">
                                            {formData.discordChannels.map((channel) => (
                                                <div key={channel.id} className={`accountModal-channel-item ${editingDiscordChannel?.id === channel.id ? "accountModal-channel-editing" : ""}`}>
                                                    {editingDiscordChannel?.id === channel.id ? (
                                                        <div className="accountModal-channel-edit-form">
                                                            <div className="accountModal-form-group">
                                                                <label htmlFor="channelId" className="accountModal-label">Channel</label>
                                                                <select
                                                                    id="channelId"
                                                                    name="channelId"
                                                                    value={tempDiscordChannel.channelId}
                                                                    onChange={handleDiscordChannelChange}
                                                                    className={`accountModal-select ${discordChannelErrors.channelId ? "accountModal-input-error" : ""}`}
                                                                >
                                                                    <option value="" disabled>Select Discord channel</option>
                                                                    {discordChannels
                                                                        .filter((channel) => {
                                                                            // Allow the currently edited channel even if it exists already.
                                                                            if (editingDiscordChannel && editingDiscordChannel.channelId === channel.id) {
                                                                                return true;
                                                                            }
                                                                            // Otherwise, exclude channels that are already in the account.
                                                                            return !formData.discordChannels.some((dc) => dc.channelId === channel.id);
                                                                        })
                                                                        .map((channel) => (
                                                                            <option key={channel.id} value={channel.id}>
                                                                                {channel.name}
                                                                            </option>
                                                                        ))}
                                                                </select>
                                                                {discordChannelErrors.channelId && (
                                                                    <span className="accountModal-error">
                                                                        <AlertCircle size={12} /> {discordChannelErrors.channelId}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="accountModal-form-group">
                                                                <label htmlFor="tradeSizePercentage" className="accountModal-label">Trade Size %</label>
                                                                <input
                                                                    type="number"
                                                                    id="tradeSizePercentage"
                                                                    name="tradeSizePercentage"
                                                                    value={tempDiscordChannel.tradeSizePercentage}
                                                                    onChange={handleDiscordChannelChange}
                                                                    className={`accountModal-input ${discordChannelErrors.tradeSizePercentage ? "accountModal-input-error" : ""}`}
                                                                    min="0"
                                                                    step="1"
                                                                />
                                                                {discordChannelErrors.tradeSizePercentage && (
                                                                    <span className="accountModal-error">
                                                                        <AlertCircle size={12} /> {discordChannelErrors.tradeSizePercentage}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="accountModal-toggle-group">
                                                                <label className="accountModal-toggle-label">
                                                                    <span>Enabled</span>
                                                                    <div className="accountModal-toggle-wrapper">
                                                                        <input
                                                                            type="checkbox"
                                                                            name="enabled"
                                                                            checked={tempDiscordChannel.enabled}
                                                                            onChange={handleDiscordChannelChange}
                                                                            className="accountModal-toggle-input"
                                                                        />
                                                                        <div className="accountModal-toggle"></div>
                                                                    </div>
                                                                </label>
                                                            </div>
                                                            <div className="accountModal-channel-actions">
                                                                <button
                                                                    type="button"
                                                                    className="accountModal-channel-action-button accountModal-save-channel-button"
                                                                    onClick={saveDiscordChannel}
                                                                >
                                                                    <Save size={16} />
                                                                    <span>Save</span>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="accountModal-channel-action-button accountModal-cancel-button"
                                                                    onClick={cancelDiscordChannelEdit}
                                                                >
                                                                    <X size={16} />
                                                                    <span>Cancel</span>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div className="accountModal-channel-info">
                                                                <div className="accountModal-channel-name">
                                                                    <span className={`accountModal-channel-status ${channel.enabled ? "accountModal-channel-status-enabled" : "accountModal-channel-status-disabled"}`}></span>
                                                                        <span>#{getChannelName(channel.channelId)} ({channel.channelId})</span>
                                                                </div>
                                                                <div className="accountModal-channel-percentage">
                                                                    {channel.tradeSizePercentage}%
                                                                </div>
                                                            </div>
                                                            <div className="accountModal-channel-actions">
                                                                <button
                                                                    type="button"
                                                                    className="accountModal-icon-button"
                                                                    onClick={() => startEditDiscordChannel(channel)}
                                                                    aria-label="Edit channel"
                                                                >
                                                                    <Edit size={16} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="accountModal-icon-button"
                                                                    onClick={() => deleteDiscordChannel(channel.id)}
                                                                    aria-label="Delete channel"
                                                                >
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="accountModal-no-channels">
                                            <p>No Discord channels configured</p>
                                        </div>
                                    )}
                                    {isAddingChannel ? (
                                        <div className="accountModal-channel-item accountModal-channel-adding">
                                            <div className="accountModal-channel-edit-form">
                                                <div className="accountModal-form-group">
                                                    <label htmlFor="newChannelId" className="accountModal-label">Channel</label>
                                                    <select
                                                        id="newChannelId"
                                                        name="channelId"
                                                        value={tempDiscordChannel.channelId}
                                                        onChange={handleDiscordChannelChange}
                                                        className={`accountModal-select ${discordChannelErrors.channelId ? "accountModal-input-error" : ""}`}
                                                    >
                                                        <option value="" disabled>Select Discord channel</option>
                                                        {discordChannels
                                                            .filter((channel) => {
                                                                // Allow the currently edited channel even if it exists already.
                                                                if (editingDiscordChannel && editingDiscordChannel.channelId === channel.id) {
                                                                    return true;
                                                                }
                                                                
                                                                // Otherwise, exclude channels that are already in the account.
                                                                return !formData.discordChannels.some((dc) => dc.channelId === channel.id);
                                                            })
                                                            .map((channel) => (
                                                                <option key={channel.id} value={channel.id}>
                                                                    {channel.name}
                                                                </option>
                                                            ))}
                                                    </select>
                                                    {discordChannelErrors.channelId && (
                                                        <span className="accountModal-error">
                                                            <AlertCircle size={12} /> {discordChannelErrors.channelId}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="accountModal-form-group">
                                                    <label htmlFor="newTradeSizePercentage" className="accountModal-label">Trade Size %</label>
                                                    <input
                                                        type="number"
                                                        id="newTradeSizePercentage"
                                                        name="tradeSizePercentage"
                                                        value={tempDiscordChannel.tradeSizePercentage}
                                                        onChange={handleDiscordChannelChange}
                                                        className={`accountModal-input ${discordChannelErrors.tradeSizePercentage ? "accountModal-input-error" : ""}`}
                                                        min="0"
                                                        step="1"
                                                    />
                                                    {discordChannelErrors.tradeSizePercentage && (
                                                        <span className="accountModal-error">
                                                            <AlertCircle size={12} /> {discordChannelErrors.tradeSizePercentage}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="accountModal-toggle-group">
                                                    <label className="accountModal-toggle-label">
                                                        <span>Enabled</span>
                                                        <div className="accountModal-toggle-wrapper">
                                                            <input
                                                                type="checkbox"
                                                                name="enabled"
                                                                checked={tempDiscordChannel.enabled}
                                                                onChange={handleDiscordChannelChange}
                                                                className="accountModal-toggle-input"
                                                            />
                                                            <div className="accountModal-toggle"></div>
                                                        </div>
                                                    </label>
                                                </div>
                                                <div className="accountModal-channel-actions">
                                                    <button
                                                        type="button"
                                                        className="accountModal-channel-action-button accountModal-save-channel-button"
                                                        onClick={saveDiscordChannel}
                                                    >
                                                        <Save size={16} />
                                                        <span>Add</span>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="accountModal-channel-action-button accountModal-cancel-button"
                                                        onClick={cancelDiscordChannelEdit}
                                                    >
                                                        <X size={16} />
                                                        <span>Cancel</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <button type="button" className="accountModal-add-channel-button" onClick={startAddDiscordChannel}>
                                            <Plus size={16} />
                                            <span>Add Discord Channel</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </section>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AccountModal;
